import React, { useState, useEffect, useRef } from 'react';
import styles from './MobileMenu.module.css';
import Langs, { Localize } from '../../app/lang/langs';

export function MobileMenu({
    menuItems,
    urlParams,
    email,
    username,
    roleType,
    getUsernameInitials,
    photoUrl,
    onEditPhoto,
    onSelectMenu,
    onLogout
}) {
    const [visible, setVisible] = useState(false)
    const [cardsReady, setCardsReady] = useState(false)
    const panelRef = useRef(null)

    useEffect(() => {
        if (visible) {
            const timer = setTimeout(() => setCardsReady(true), 80)
            return () => clearTimeout(timer)
        } else {
            setCardsReady(false)
        }
    }, [visible])

    // Close on back button
    useEffect(() => {
        const handleBack = (e) => {
            if (visible) {
                e.preventDefault()
                setVisible(false)
            }
        }
        window.addEventListener('popstate', handleBack)
        return () => window.removeEventListener('popstate', handleBack)
    }, [visible])

    function handleSelect(value) {
        setVisible(false)
        setTimeout(() => onSelectMenu(value), 300)
    }

    // The sheet mirrors the rail's shape rather than dissolving it: a group whose sub-items
    // are routes of their own ("Training & Skills", "HR", Settings) becomes a titled section
    // of cards, and everything else — top-level destinations, and groups whose sub-items are
    // only sub-views of one page, like Congés — stays a single card.
    //
    // Ungrouped destinations come first and unheaded: they are the pages people open all day,
    // and giving them a heading of their own would need a label that means nothing ("Other").
    const loose = []
    const sections = []
    menuItems.filter(item => item.visible).forEach(item => {
        const subItems = item.subItems || []
        if (subItems.some(sub => sub.view)) {
            // A routed group. Its header is not a destination — `hr` and `training` are not
            // routes — so a group left with nothing visible under it is dropped entirely
            // rather than falling through and becoming a card that navigates nowhere.
            const routed = subItems.filter(sub => sub.view && sub.visible)
            if (routed.length) sections.push({ ...item, items: routed })
            return
        }
        loose.push(item)
    })

    // The reveal stagger runs across the whole sheet, not per section, so the cards still
    // come in as one wave.
    let cardIndex = 0

    // One card. `value` is always the routed value — for a section it is the sub-item's, so
    // selection is unchanged by the grouping.
    const renderCard = (item) => {
        const isActive = urlParams.view && String(urlParams.view).toLowerCase() === String(item.value).toLowerCase()
        const Icon = item.icon
        const delay = cardIndex++ * 40

        return (
            <div
                key={"mobile_menu_" + item.value}
                className={
                    `${styles.card} ${cardsReady ? styles.cardVisible : ''} ${isActive ? styles.cardActive : ''}`
                }
                style={{ transitionDelay: cardsReady ? `${delay}ms` : '0ms' }}
                onClick={() => handleSelect(item.value)}>
                <Icon className={styles.cardIcon} />
                <span className={styles.cardLabel}>
                    <Langs str={item.label} />
                </span>
            </div>
        )
    }

    return (
        <>
            {/* FAB toggle button */}
            <button
                className={`${styles.fab} ${visible ? styles.fabOpen : ''}`}
                onClick={() => setVisible(!visible)}
                aria-label={visible ? "Close Menu" : "Open Menu"}>
                <span className={styles.fabIcon}>
                    {visible ? '+' : <i className="pi pi-th-large" />}
                </span>
            </button>

            {/* Backdrop overlay */}
            <div
                className={`${styles.overlay} ${visible ? styles.overlayVisible : ''}`}
                onClick={() => setVisible(false)}
            />

            {/* Bottom sheet panel */}
            <div
                ref={panelRef}
                className={`${styles.panel} ${visible ? styles.panelVisible : ''}`}>

                {/* Drag handle */}
                <div className={styles.handle} />

                {/* Profile header */}
                <div className={styles.profileHeader}>
                    {/* Same control as the rail's avatar: it shows the picture on file and
                        opens the editor. It looked tappable and pressed like a button before
                        without being wired to anything. */}
                    <button
                        className={`${styles.avatar} ${photoUrl ? styles.avatarPhoto : ''}`}
                        onClick={() => { setVisible(false); if (onEditPhoto) onEditPhoto() }}
                        aria-label={Localize('PHOTO_EDIT_TITLE')}>
                        {photoUrl
                            ? <img
                                src={photoUrl}
                                alt=""
                                // A picture that 404s leaves a broken-image glyph in the
                                // sheet otherwise.
                                onError={(e) => { e.currentTarget.style.display = 'none' }}
                                className={styles.avatarImg}
                            />
                            : getUsernameInitials()}
                    </button>
                    <div className={styles.profileInfo}>
                        <span className={styles.profileName}>
                            {username && username !== "null" ? username : email}
                        </span>
                        <span className={styles.profileRole}>{roleType}</span>
                    </div>
                    <button
                        className={styles.logoutBtn}
                        onClick={() => {
                            setVisible(false)
                            onLogout()
                        }}
                        aria-label="Logout">
                        <i className="pi pi-sign-out" />
                    </button>
                </div>

                {/* One scroller for the whole list, so the sections scroll together rather
                    than each becoming its own little scroll box. */}
                <div className={styles.scroller}>
                    {loose.length ? (
                        <div className={styles.grid}>
                            {loose.map(renderCard)}
                        </div>
                    ) : null}

                    {sections.map(section => {
                        const SectionIcon = section.icon
                        return (
                            <div key={"mobile_section_" + section.value}>
                                {/* A caption, not a control: the group itself is not a page,
                                    and a row that looks tappable and goes nowhere is worse
                                    than no row. */}
                                <div className={styles.sectionHeader}>
                                    {SectionIcon
                                        ? <SectionIcon fontSize="inherit" sx={{ color: '#FF9015' }} />
                                        : null}
                                    <span className={styles.sectionTitle}>
                                        <Langs str={section.label} />
                                    </span>
                                    <span className={styles.sectionRule} />
                                </div>
                                <div className={styles.grid}>
                                    {section.items.map(renderCard)}
                                </div>
                            </div>
                        )
                    })}
                </div>

                {/* Safe area spacer */}
                <div className={styles.safeArea} />
            </div>
        </>
    )
}
